import type { AppContextInterface } from '@aurora/shared-client/components/context/AppContext/AppContext';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import useMessagePolicies from '@aurora/shared-client/components/messages/useMessagePolicies';
import useNodePolicies from '@aurora/shared-client/components/nodes/useNodePolicies';
import { canModerateNode } from '@aurora/shared-client/helpers/nodes/NodePolicyHelper';
import type { ManageContentPageAndParams } from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type {
  BlogTopicMessage,
  TkbTopicMessage
} from '@aurora/shared-generated/types/graphql-schema-types';
import { ConversationStyle } from '@aurora/shared-generated/types/graphql-schema-types';
import {
  EndUserComponent,
  EndUserPages,
  EndUserQueryParams
} from '@aurora/shared-types/pages/enums';
import IdConverter from '@aurora/shared-utils/graphql/IdConverter/IdConverter';
import { checkPolicy } from '@aurora/shared-utils/helpers/objects/PolicyResultHelper';
import React, { useContext } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type { MessageViewFragment } from '../../../types/graphql-types';
import useTranslation from '../../useTranslation';
import { MessageListTabItem } from '../MessageListTabs/MessageListTabs';
import localStyles from './MessageCommentBanner.module.pcss';

interface Props {
  /**
   * Message to check author property
   */
  message: MessageViewFragment;
  /**
   * Whether the message is in pre mod state.
   */
  isPreModArticle: boolean;
}

/**
 * Renders the banner bar for a message comment.
 */
const MessageCommentBanner: React.FC<React.PropsWithChildren<Props>> = ({
  message,
  isPreModArticle
}) => {
  const cx = useClassNameMapper(localStyles);
  const { authUser, community } = useContext<AppContextInterface>(AppContext);

  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.MESSAGE_COMMENT_BANNER
  );

  const { router, Link } = useEndUserRoutes();

  const { data: nodePolicies, loading: nodePoliciesLoading } = useNodePolicies(module, {
    id: community.id,
    useCanModerateNode: true
  });

  const { id, board } = message;
  const isBlog = board?.conversationStyle === ConversationStyle.Blog;
  const isTkb = board?.conversationStyle === ConversationStyle.Tkb;

  const { data: messagePoliciesData, loading: messagePoliciesLoading } = useMessagePolicies(
    module,
    {
      id: id,
      useCanDoAuthoringActionsOnBlog: isBlog,
      useCanDoAuthoringActionsOnTkb: isTkb
    },
    !message || IdConverter.isOptimistic(message?.id)
  );

  if (nodePoliciesLoading || textLoading || messagePoliciesLoading) {
    return null;
  }

  const canAccessModerationManagerPolicy = canModerateNode(nodePolicies?.coreNode);
  const canDoAuthoringActions = isBlog
    ? checkPolicy(
        (messagePoliciesData?.message as BlogTopicMessage)?.blogMessagePolicies
          ?.canDoAuthoringActionsOnBlog
      )
    : isTkb
    ? checkPolicy(
        (messagePoliciesData?.message as TkbTopicMessage)?.tkbMessagePolicies
          ?.canDoAuthoringActionsOnTkb
      )
    : authUser?.id === message?.author?.id;

  const queryParams = {
    [EndUserQueryParams.TAB]: router.getUnwrappedQueryParam(
      EndUserQueryParams.TAB,
      MessageListTabItem.MODERATION
    )
  };

  /**
   * Renders the link to spam dashboard.
   */
  function renderDashboardLink(): React.ReactElement {
    return (
      canAccessModerationManagerPolicy && (
        <Link<ManageContentPageAndParams>
          route={EndUserPages.ManageContentPage}
          query={queryParams}
          legacyBehavior={true}
        >
          {formatMessage('manageModeration')}
        </Link>
      )
    );
  }

  return (
    (isPreModArticle || canAccessModerationManagerPolicy) && (
      <div className={cx('lia-banner')} data-testid="MessageBannerReply">
        <span>
          {formatMessage(
            `${canDoAuthoringActions ? 'preModCommentAuthorText' : 'preModCommentModeratorText'}`
          )}
        </span>
        {renderDashboardLink()}
      </div>
    )
  );
};

export default MessageCommentBanner;
